/**
 * Orgs / Members / Invites API client. Thin fetch wrappers around
 * tile-backend's /api/orgs/* and /api/invites/* endpoints. See
 * tile-backend/src/routes/org.ts for the full route table.
 *
 * Every call goes through `apiFetch` (utils/api/client.ts) which injects
 * the `X-Active-Org` header from the orgs Redux slice when one is set.
 */
import { apiFetch } from '@/services/http-client/client';

export interface Org {
  id: string;
  name: string;
  slug: string;
  ownerId: string | null;
  deletedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface OrgListing {
  id: string;
  name: string;
  slug: string;
  ownerId: string | null;
  myRole: 'owner' | 'admin' | 'developer' | 'viewer';
}

export interface Member {
  userId: string;
  email: string;
  name: string | null;
  /** Identifier matching a role option from `rolesApi.list`: a bare slug for
   *  built-in roles, or the custom role's id. This is what setMemberRole takes. */
  roleId: string;
  /** Human-readable role name for display (e.g. "Admin", "Release Manager"). */
  roleName: string;
  /** True for the five built-in roles; false for org-defined custom roles. */
  isBuiltin: boolean;
  grantedAt: string;
}

export interface Policy {
  id: string;
  name: string;
  description: string | null;
  actions: string[];
  isBuiltin: boolean;
  orgId: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface RoleSummary {
  id: string;
  name: string;
  description: string | null;
  orgId: string | null;
  isBuiltin: boolean;
  policyIds: string[];
  memberCount: number;
}

export interface InviteSummary {
  id: string;
  roleSlug: 'admin' | 'developer' | 'pm' | 'viewer';
  email: string | null;
  isMultiUse: boolean;
  maxUses: number | null;
  useCount: number;
  expiresAt: string | null;
  createdAt: string;
  revoked: boolean;
  used: boolean;
}

export interface SlugCheck {
  available: boolean;
  reason?: 'too_short' | 'too_long' | 'invalid_chars' | 'reserved' | 'taken' | 'empty';
  message?: string;
}

export const orgsApi = {
  listMine: () => apiFetch<{ orgs: OrgListing[] }>('/api/orgs'),

  /** Live slug availability check for the create + rename forms. */
  checkSlug: (slug: string, excludeOrgId?: string) =>
    apiFetch<SlugCheck>(
      `/api/orgs/check-slug?slug=${encodeURIComponent(slug)}${
        excludeOrgId ? `&excludeOrgId=${encodeURIComponent(excludeOrgId)}` : ''
      }`,
    ),

  create: (input: { name: string; slug?: string }) =>
    apiFetch<{ org: Org }>('/api/orgs', { method: 'POST', body: input }),

  bySlug: (slug: string) => apiFetch<{ org: Org }>(`/api/orgs/${encodeURIComponent(slug)}`),

  update: (slug: string, input: { name?: string; slug?: string }) =>
    apiFetch<{ org: Org }>(`/api/orgs/${encodeURIComponent(slug)}`, {
      method: 'PATCH',
      body: input,
    }),

  remove: (slug: string) =>
    apiFetch<void>(`/api/orgs/${encodeURIComponent(slug)}`, { method: 'DELETE' }),

  /** Apps + per-app envs used by the Permissions scope picker. */
  resources: (slug: string) =>
    apiFetch<{
      apps: { id: string; name: string }[];
      envsByAppId: Record<string, { id: string; name: string; kind: string }[]>;
    }>(`/api/orgs/${encodeURIComponent(slug)}/resources`),

  members: (slug: string) =>
    apiFetch<{ members: Member[] }>(`/api/orgs/${encodeURIComponent(slug)}/members`),

  /** `roleId` is a built-in slug (owner/admin/developer/pm/viewer) or a custom
   *  role's id (from rolesApi.list). The backend resolves either form. */
  setMemberRole: (slug: string, userId: string, roleId: string) =>
    apiFetch<{ userId: string; roleId: string }>(
      `/api/orgs/${encodeURIComponent(slug)}/members/${encodeURIComponent(userId)}`,
      { method: 'PATCH', body: { role: roleId } },
    ),

  removeMember: (slug: string, userId: string) =>
    apiFetch<void>(`/api/orgs/${encodeURIComponent(slug)}/members/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    }),

  invites: {
    list: (slug: string) =>
      apiFetch<{ invites: InviteSummary[] }>(`/api/orgs/${encodeURIComponent(slug)}/invites`),

    create: (
      slug: string,
      input: {
        roleSlug: 'admin' | 'developer' | 'pm' | 'viewer';
        email?: string;
        isMultiUse?: boolean;
        maxUses?: number;
        expiresInDays?: number | null;
      },
    ) =>
      apiFetch<{
        invite: InviteSummary & { orgSlug: string };
        url: string;
        note: string;
        /** Email invites only: whether the invite email went out. */
        emailSent?: boolean;
        emailError?: 'not_configured' | 'send_failed';
      }>(`/api/orgs/${encodeURIComponent(slug)}/invites`, {
        method: 'POST',
        body: input,
      }),

    revoke: (slug: string, id: string) =>
      apiFetch<void>(`/api/orgs/${encodeURIComponent(slug)}/invites/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),

    redeem: (token: string) =>
      apiFetch<{
        redeemed: true;
        org: { id: string; name: string; slug: string } | null;
        role: string;
      }>(`/api/invites/${encodeURIComponent(token)}/redeem`, { method: 'POST' }),
  },
};

/**
 * IAM policies + roles client. These hit tile-auth directly through the
 * gateway/backend proxy path `/api/auth/*` (same as the existing
 * /check + /me + /grants endpoints).
 */
export const policiesApi = {
  list: (orgId?: string) =>
    apiFetch<{ policies: Policy[] }>(
      `/api/auth/policies${orgId ? `?orgId=${encodeURIComponent(orgId)}` : ''}`,
    ),
  get: (id: string) => apiFetch<{ policy: Policy }>(`/api/auth/policies/${encodeURIComponent(id)}`),
  create: (input: { name: string; description?: string; actions: string[]; orgId: string }) =>
    apiFetch<{ policy: Policy }>('/api/auth/policies', { method: 'POST', body: input }),
  update: (id: string, input: { name?: string; description?: string; actions?: string[] }) =>
    apiFetch<{ policy: Policy }>(`/api/auth/policies/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    }),
  remove: (id: string) =>
    apiFetch<void>(`/api/auth/policies/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

export const rolesApi = {
  list: (orgId?: string) =>
    apiFetch<{ roles: RoleSummary[] }>(
      `/api/auth/roles${orgId ? `?orgId=${encodeURIComponent(orgId)}` : ''}`,
    ),
  get: (id: string) => apiFetch<{ role: RoleSummary }>(`/api/auth/roles/${encodeURIComponent(id)}`),
  create: (input: { name: string; description?: string; orgId: string; policyIds?: string[] }) =>
    apiFetch<{ role: RoleSummary }>('/api/auth/roles', { method: 'POST', body: input }),
  update: (id: string, input: { name?: string; description?: string; policyIds?: string[] }) =>
    apiFetch<{ role: RoleSummary }>(`/api/auth/roles/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    }),
  remove: (id: string) =>
    apiFetch<void>(`/api/auth/roles/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  attach: (id: string, policyIds: string[]) =>
    apiFetch<{ roleId: string; policyIds: string[] }>(
      `/api/auth/roles/${encodeURIComponent(id)}/policies`,
      { method: 'POST', body: { policyIds } },
    ),
  detach: (id: string, policyId: string) =>
    apiFetch<void>(
      `/api/auth/roles/${encodeURIComponent(id)}/policies/${encodeURIComponent(policyId)}`,
      { method: 'DELETE' },
    ),
};
